import mergeWith from 'lodash/mergeWith';
import groupBy from 'lodash/groupBy';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import {
  isRovoOrgProduct,
  isRovoInstanceProduct,
  isCollectionProductEntitlement,
  isCollectionInstanceEntitlement,
  isEnterpriseInstanceOffering,
  isMultiInstanceProductOffering,
  isEnterpriseOffering,
} from '@css-xp/ppc-utils';
import { transformEntitlementToCartProduct } from '@css-xp/ppc-purchase-experience';
import { isAccessProduct, isJiraProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import type { State } from '../../../store/types';
import { transformEntitlementToCartLinkedEntitlement, truthy } from '../../../utils';
import { OperationType, ProductSubscriptionsWrapper, ProductSubscription } from '../../../types';

type StoreData = Pick<
  State,
  | 'products'
  | 'linkedEntitlements'
  | 'sandboxApps'
  | 'sandboxProducts'
  | 'inactiveProducts'
  | 'inactiveApps'
  | 'inactiveAddons'
  | 'addons'
>;
type StoreProduct = State['products'][keyof State['products']];

const FamilyContainer = generatedTypesNew.RelationshipPathType.FAMILY_CONTAINER;
const APP_DEPENDENCE = generatedTypesNew.RelationshipPathType.APP_DEPENDENCE;
const ADDON_DEPENDENCE = 'ADDON_DEPENDENCE'; // FIXME :: RelationType to be picked from graphtypes
const BillingFrequency = generatedTypesNew.BillingFrequency;
const BillingStatus = generatedTypesNew.BillingStatus;

const getJiraContainerHavingAddons = (products: ProductSubscription[]) => {
  const jiraContainerOrProductIdMap: Record<string, 1> = {};
  products.forEach((product) => {
    const isAddonParent = product.relatesToEntitlements?.find(
      // @ts-ignore
      (relatesTo) => relatesTo?.relationshipType === ADDON_DEPENDENCE
    );
    if (isAddonParent) {
      const hasActiveLinkedAddOn =
        products?.find((productInfo) =>
          productInfo?.relatesFromEntitlements?.find(
            (relatesFrom) =>
              // @ts-ignore
              relatesFrom?.relationshipType === ADDON_DEPENDENCE && relatesFrom?.entitlementId === product?.id
          )
        )?.billingStatus === generatedTypesNew.BillingStatus.ACTIVE;

      if (isJiraProduct(product.productId)) {
        const containerInfo = product?.relatesFromEntitlements?.find(
          (relatesFrom) => relatesFrom?.relationshipType === generatedTypesNew.RelationshipPathType.FAMILY_CONTAINER
        );
        if (containerInfo && containerInfo.entitlementId && hasActiveLinkedAddOn) {
          jiraContainerOrProductIdMap[containerInfo.entitlementId] = 1;
        }
      }
      if (isAccessProduct(product.productId) && hasActiveLinkedAddOn) {
        jiraContainerOrProductIdMap[product.id] = 1;
      }
    }
  });
  return jiraContainerOrProductIdMap;
};

const isAddonLinkedToEntitlement = (node: ProductSubscription, containerOrProductHavingAddons: Record<string, 1>) => {
  if (isJiraProduct(node.productId)) {
    return node?.relatesFromEntitlements?.some(
      (relatesTo) =>
        relatesTo?.relationshipType === generatedTypesNew.RelationshipPathType.FAMILY_CONTAINER &&
        relatesTo.entitlementId &&
        containerOrProductHavingAddons[relatesTo.entitlementId]
    );
  }
  if (isAccessProduct(node.productId)) {
    return containerOrProductHavingAddons[node.id];
  }
};

const getSelectedPricingPlanId = (
  product: ProductSubscription,
  offeringsAndpricingPlans: State['offeringsAndpricingPlans']
): string | undefined => {
  const { productId } = product;

  if (!offeringsAndpricingPlans || !productId) return product.pricingPlanId ?? '';

  const offeringsForProduct = offeringsAndpricingPlans[productId] ?? [];
  const selectedOffering = offeringsForProduct.find(({ id }) => id === product.offeringId);

  const pricingPlanType = product?.pricingPlan?.type ?? 'COMMERCIAL';
  const selectedPricingPlanId = selectedOffering?.defaultPricingPlansEligibilityMap?.[pricingPlanType];

  return selectedPricingPlanId;
};

export const InactiveReasonStrings = {
  INACTIVE: 'Contact the Partner Service Desk for support with cancelled subscriptions',
  ANNUAL: 'Contact the Partner service desk for support with annual subscriptions.',
  FUTURE_DATED: 'This subscription will start in future, you can’t change this entitlement.',
  PRICING_ELIGIBILITY: 'This subscription is on different price eligibility than destination billing profile',
  CURRENCY: 'This subscription is on different currency than destination billing profile',
  ADDONS:
    'Either this subscription or one of its family members contains an add-on. The add-on self-serve feature is currently not supported.',
  GENERIC: 'Contact Partner Service Desk for support with subscription.',
};

const getInactiveReason = (
  node: ProductSubscription,
  config: {
    hasAddons: boolean;
    hasFutureDateSubscription: boolean;
    destinationPricingEligibility?: string;
    destinationCurrency?: string;
  }
) => {
  if (node.billingStatus === BillingStatus.INACTIVE) {
    return InactiveReasonStrings.INACTIVE;
  }
  if (node.billingFrequency === BillingFrequency.ANNUAL) {
    return InactiveReasonStrings.ANNUAL;
  }
  if (node.isFutureDatedSubscription) {
    return InactiveReasonStrings.FUTURE_DATED;
  }
  if (config.hasAddons) {
    return InactiveReasonStrings.ADDONS;
  }

  if (
    config.destinationPricingEligibility &&
    node.pricingPlan &&
    config.destinationPricingEligibility.toLowerCase() !== node.pricingPlan?.type?.toLowerCase()
  ) {
    return InactiveReasonStrings.PRICING_ELIGIBILITY;
  }

  if (
    config.destinationCurrency &&
    node.pricingPlan &&
    config.destinationCurrency.toLowerCase() !== node.pricingPlan?.currency?.toLowerCase()
  ) {
    return InactiveReasonStrings.CURRENCY;
  }
  return InactiveReasonStrings.GENERIC;
};

// FIXME :: Remove the eslint disable during FF cleanup
const splitToDifferentSubscriptions = (
  productSubscriptions: ProductSubscription[],
  showSandboxProducts?: boolean,
  isSandboxDecouplingEnabled?: boolean,
  isFutureDatedEntitlementEnabled?: boolean,
  isMultiCurrencyEnabled?: boolean,
  destinationCurrency?: string,
  destinationPricingEligibility?: string,
  isNonCommercialPricingEnabled?: boolean,
  operationType?: OperationType,
  isTWCPhase1Enabled?: boolean,
  isTWCPhase2Enabled?: boolean,
  isAddOnsPhase2Enabled?: boolean,
  isMultiInstanceAppsEnabled?: boolean
  // eslint-disable-next-line max-params
) => {
  const sandboxProducts: ProductSubscription[] = [];
  const sandboxApps: ProductSubscription[] = [];
  const linkedEntitlements: ProductSubscription[] = [];
  const products: ProductSubscription[] = [];
  const orgLevelProducts: ProductSubscription[] = [];
  const inactiveOrgLevelProducts: ProductSubscription[] = [];
  const inactiveProducts: ProductSubscription[] = [];
  const inactiveApps: ProductSubscription[] = [];
  const inactiveAddons: ProductSubscription[] = [];
  const addons: ProductSubscription[] = [];

  const containerOrProductHavingAddons = getJiraContainerHavingAddons(productSubscriptions);
  productSubscriptions.filter(truthy).forEach((node) => {
    /**
     * In UOF we are grouping the products by orgName, in order to include the org products.
     * From back-end we get orgId/orgName in response for all products, hence in order to include
     * only org product we need to check if the product is an Access product.
     * if add on phase2 is enabled, then we don't need to consider inactive products for parent entitlement if it has add-on.
     */
    const isAddonLinkedWithEntitlement =
      !isAddOnsPhase2Enabled && isAddonLinkedToEntitlement(node, containerOrProductHavingAddons);

    /**
     * In order to show the collection product and its instances in disabled state, and
     * in order to avoid the collection product and its instances to be shown in the cart,
     * adding Collection parent product and its instances in InactiveProducts queue.
     *
     * Since the collection product is an org level product, it will be shown in the org level products.
     */
    if (
      node.billingStatus === BillingStatus.INACTIVE ||
      node.billingFrequency === BillingFrequency.ANNUAL ||
      isAddonLinkedWithEntitlement ||
      (isFutureDatedEntitlementEnabled && node.isFutureDatedSubscription) ||
      (isMultiCurrencyEnabled &&
        destinationCurrency &&
        node.pricingPlan &&
        destinationCurrency.toLowerCase() !== node.pricingPlan.currency?.toLowerCase()) ||
      (isNonCommercialPricingEnabled &&
        operationType === OperationType.OWN &&
        destinationPricingEligibility &&
        node.pricingPlan &&
        destinationPricingEligibility.toLowerCase() !== node.pricingPlan.type?.toLowerCase()) ||
      (isTWCPhase1Enabled &&
        !isTWCPhase2Enabled &&
        (isCollectionProductEntitlement(node.productId) ||
          isCollectionInstanceEntitlement(node.relatesFromEntitlements))) ||
      isEnterpriseInstanceOffering(node?.edition) ||
      isEnterpriseOffering(node?.edition) ||
      (isMultiInstanceAppsEnabled &&
        !!node?.productId &&
        isMultiInstanceProductOffering(node?.offering, node.productId))
    ) {
      if (
        isAccessProduct(node.productId) ||
        isRovoOrgProduct(node) ||
        isCollectionProductEntitlement(node.productId) ||
        isEnterpriseOffering(node?.edition) ||
        (isMultiInstanceAppsEnabled &&
          !!node?.productId &&
          isMultiInstanceProductOffering(node?.offering, node.productId))
      ) {
        inactiveOrgLevelProducts.push(node);
        // @ts-ignore
        node.inactiveReason = getInactiveReason(node, {
          hasAddons: !!isAddonLinkedWithEntitlement,
          hasFutureDateSubscription: !!isFutureDatedEntitlementEnabled,
          destinationPricingEligibility,
          destinationCurrency,
        });
        return;
      }
      if (node.isApp) {
        inactiveApps.push(node);
        return;
      }
      if (node.isAddOn) {
        // this will store the add-ons which are inactive and part of parent
        inactiveAddons.push(node);
        return;
      }
      // this will store the add-on parents, annual and inactive products
      inactiveProducts.push(node);
      // @ts-ignore
      node.inactiveReason = getInactiveReason(node, {
        hasAddons: !!isAddonLinkedWithEntitlement,
        hasFutureDateSubscription: !!isFutureDatedEntitlementEnabled,
        destinationPricingEligibility,
        destinationCurrency,
      });
      return;
    }
    // Added isCollectionProductEntitlement(node.productId) check to make collection product as isOrgProduct true
    if (
      isAccessProduct(node.productId) ||
      isRovoOrgProduct(node) ||
      (isTWCPhase2Enabled && isCollectionProductEntitlement(node.productId))
    ) {
      orgLevelProducts.push(node);
      return;
    }
    if (node.isAddOn) {
      addons.push(node);
      return;
    }
    if (!node.cloudSiteHostname) {
      return;
    }
    /**
     * If showSandboxProducts is true, excluding the sandbox products to be stored in sandboxProducts in store.
     * Instead storing the sandbox products in products, and apps in apps.
     *
     * Still storing the Rovo instance product.
     */
    if (node.isSandbox && !isSandboxDecouplingEnabled && (!showSandboxProducts || isRovoInstanceProduct(node))) {
      if (node.isApp) {
        sandboxApps.push(node);
      } else {
        sandboxProducts.push(node);
      }
      return;
    }
    if (node.isApp) {
      linkedEntitlements.push(node);
      return;
    }

    products.push(node);
  });

  return {
    linkedEntitlements,
    products,
    sandboxProducts,
    sandboxApps,
    orgLevelProducts,
    inactiveProducts,
    inactiveApps,
    inactiveOrgLevelProducts,
    inactiveAddons,
    addons,
  };
};

export const getDataForStore = ({
  entitlementSubscriptionsWrapper,
  offeringsAndpricingPlans,
  operationType,
  hasAllData = true,
  includeOrgProducts = false,
  showSandboxProducts = false,
  isSandboxDecouplingEnabled = false,
  isFutureDatedEntitlementEnabled = false,
  isMultiCurrencyEnabled = false,
  destinationCurrency,
  isNonCommercialPricingEnabled = false,
  destinationPricingEligibility,
  isTWCPhase1Enabled = false,
  isTWCPhase2Enabled = false,
  isAddOnsPhase2Enabled = false,
  isMultiInstanceAppsEnabled = false,
}: {
  entitlementSubscriptionsWrapper: ProductSubscriptionsWrapper;
  offeringsAndpricingPlans: State['offeringsAndpricingPlans'];
  operationType: OperationType;
  hasAllData?: boolean;
  includeOrgProducts?: boolean;
  showSandboxProducts?: boolean;
  isSandboxDecouplingEnabled?: boolean;
  isFutureDatedEntitlementEnabled?: boolean;
  isMultiCurrencyEnabled?: boolean;
  destinationCurrency?: string;
  isNonCommercialPricingEnabled?: boolean;
  destinationPricingEligibility?: string;
  isTWCPhase1Enabled?: boolean;
  isTWCPhase2Enabled?: boolean;
  isAddOnsPhase2Enabled?: boolean;
  isMultiInstanceAppsEnabled?: boolean;
}): StoreData | null => {
  const entitlementSubscriptions = entitlementSubscriptionsWrapper?.nodes;

  if (!entitlementSubscriptions) {
    return null;
  }

  const {
    linkedEntitlements,
    products,
    sandboxProducts,
    sandboxApps,
    orgLevelProducts,
    inactiveProducts,
    inactiveApps,
    inactiveOrgLevelProducts,
    addons,
    inactiveAddons,
  } = splitToDifferentSubscriptions(
    entitlementSubscriptions as ProductSubscription[],
    showSandboxProducts,
    isSandboxDecouplingEnabled,
    isFutureDatedEntitlementEnabled,
    isMultiCurrencyEnabled,
    destinationCurrency,
    destinationPricingEligibility,
    isNonCommercialPricingEnabled,
    operationType,
    isTWCPhase1Enabled,
    isTWCPhase2Enabled,
    isAddOnsPhase2Enabled,
    isMultiInstanceAppsEnabled
  );

  const refineApp = (app: ProductSubscription) => {
    const relatesFromEntitlements = app.relatesFromEntitlements || [];
    const containerRelationship = relatesFromEntitlements.find(
      (relation) => relation?.relationshipType === APP_DEPENDENCE
    );
    // it could be a family container id(Jira Container id) or non-family product id(Confluence id)
    const containerOrProductId = containerRelationship?.entitlementId ?? '';
    return transformEntitlementToCartLinkedEntitlement({
      entitlement: app,
      containerOrProductId,
      operationType,
      hasAllData,
    });
  };

  const refineAddon = (addon: ProductSubscription) => {
    const relatesFromEntitlements = addon.relatesFromEntitlements || [];
    const addonParentProduct = relatesFromEntitlements.find(
      // @ts-ignore
      (relation) => relation?.relationshipType === ADDON_DEPENDENCE
    );

    const parentAddon = (() => {
      if (isAddOnsPhase2Enabled) {
        // merge all products and orgLevelProducts to get the parent entitlement id
        const allProducts = [...products, ...orgLevelProducts, ...inactiveOrgLevelProducts];

        // for add-on phase 2, we have to find the entitlement id of the parent product because add-on had dependency of parent entitlement id not container id
        const entObj = allProducts?.find((product) => product.id === addonParentProduct?.entitlementId);
        // If relatesFromEntitlements exists and has "relationshipType": "FAMILY_CONTAINER", return its entitlementId
        const familyContainer = entObj?.relatesFromEntitlements?.find(
          (ent) => ent?.relationshipType === 'FAMILY_CONTAINER'
        );
        if (familyContainer) {
          return familyContainer?.entitlementId ?? '';
        }

        return entObj?.id ?? '';
      }
      return addonParentProduct?.entitlementId ?? '';
    })();

    /**
     * we are converting productId = offeringId for statuspage addons and saving in store.
     * we are making this change because all statuspage addons have same product id
     * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
     */
    if (isStatuspage(addon.productId)) {
      addon.productId = addon.offeringId;
      addon.productName = addon.edition;
      //@ts-ignore
      addon.offering.name = 'Add-ons';
    }
    addon.activeUsers = addon.activeUsers ? addon.activeUsers : 1;
    return transformEntitlementToCartLinkedEntitlement({
      entitlement: addon,
      containerOrProductId: parentAddon,
      operationType,
      hasAllData,
      isAddOn: true,
    });
  };

  const refineProduct = (product: ProductSubscription) => {
    const relatesFromEntitlements = product.relatesFromEntitlements ?? [];
    const familyContainerRelationship = relatesFromEntitlements.find(
      (relation) => relation?.relationshipType === FamilyContainer
    );
    const containerId = familyContainerRelationship?.entitlementId;

    const isPricingPlanTypeNotDefault = product.pricingPlan?.pricingPlanType !== 'DEFAULT_PRICING';

    const selectedPricingPlanId =
      isPricingPlanTypeNotDefault && product.pricingPlanId
        ? product.pricingPlanId
        : getSelectedPricingPlanId(product, offeringsAndpricingPlans);

    /**
     * In order to identify product is an instance of any parent product, we are adding parentEntitlementId
     * For current implementation adding parentEntitlementId for Collection product.
     *
     * Future enhancement: We can add parentEntitlementId for other product types (Enterprise instance) as well.
     */
    const parentEntitlementId =
      product.relatesFromEntitlements?.find(
        (relation) => (relation?.relationshipType as unknown) === SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
      )?.entitlementId ?? '';

    return transformEntitlementToCartProduct({
      entitlement: product,
      selectedPricingPlanId,
      containerId,
      operationType,
      hasAllData,
      parentEntitlementId,
    });
  };

  const prepareOrgProducts = (
    products: ProductSubscription[],
    offeringsAndPricingPlans: State['offeringsAndpricingPlans']
  ) => {
    return products.map((product) => {
      const isPricingPlanTypeNotDefault = product.pricingPlan?.pricingPlanType !== 'DEFAULT_PRICING';

      const selectedPricingPlanId =
        isPricingPlanTypeNotDefault && product.pricingPlanId
          ? product.pricingPlanId
          : getSelectedPricingPlanId(product, offeringsAndPricingPlans);

      return transformEntitlementToCartProduct({
        entitlement: product,
        selectedPricingPlanId,
        containerId: null,
        operationType,
        hasAllData,
        includeOrgProducts,
        organizationId: product.orgId,
        organisationName: product.orgName,
        isOrgProduct: true,
      });
    });
  };

  const updatedProductSubscriptions: StoreProduct[] = products.map(refineProduct);
  const updatedSandboxProducts: StoreProduct[] = sandboxProducts.map(refineProduct);
  const updatedInactiveProducts: StoreProduct[] = inactiveProducts.map(refineProduct);
  if (includeOrgProducts) {
    updatedProductSubscriptions.push(...prepareOrgProducts(orgLevelProducts, offeringsAndpricingPlans));
    updatedInactiveProducts.push(...prepareOrgProducts(inactiveOrgLevelProducts, offeringsAndpricingPlans));
  }

  const mergeByProductId = (productsMap: State['products'], currentProduct: StoreProduct) => {
    productsMap[currentProduct.id] = currentProduct;
    return productsMap;
  };

  const productsMap = updatedProductSubscriptions.reduce(mergeByProductId, {});
  const sandboxProductsMap = updatedSandboxProducts.reduce(mergeByProductId, {});
  const inactiveProductsMap = updatedInactiveProducts.reduce(mergeByProductId, {});

  const refinedAppSubscription = linkedEntitlements.map(refineApp);

  const refinedSandboxApps = sandboxApps.map(refineApp);

  const refinedInactiveApps = inactiveApps.map(refineApp);

  const appsMap = groupBy(refinedAppSubscription, 'containerOrProductId');
  const sandboxAppsMap = groupBy(refinedSandboxApps, 'containerOrProductId');
  const inactiveAppsMap = groupBy(refinedInactiveApps, 'containerOrProductId');

  const refinedAddons = addons.map(refineAddon);

  const refinedInactiveAddons = inactiveAddons.map(refineAddon);

  const addonsMap = groupBy(refinedAddons, 'containerOrProductId');

  const inactiveAddonsMap = groupBy(refinedInactiveAddons, 'containerOrProductId');
  // when isAddOnsPhase2Enabled is enabled, we have to merge addonsMap with appsMap to get the linkedEntitlements commonly
  const mergedLinkedEntitlements = mergeWith(appsMap, isAddOnsPhase2Enabled ? addonsMap : {}, (objValue, srcValue) => {
    if (Array.isArray(objValue)) {
      return objValue.concat(srcValue);
    }
  });

  return {
    products: productsMap,
    linkedEntitlements: mergedLinkedEntitlements,
    sandboxProducts: sandboxProductsMap,
    sandboxApps: sandboxAppsMap,
    inactiveApps: inactiveAppsMap,
    inactiveProducts: inactiveProductsMap,
    addons: !isAddOnsPhase2Enabled ? addonsMap : {},
    inactiveAddons: inactiveAddonsMap,
  };
};
